import {
    calculateEntryMetrics,
    calculateMealMetrics,
    getRoundedCalculations,
    getRoundedQuantity,
} from "@client/calculations";
import {EntryIcon} from "@client/components/EntryIcon";
import {Icon} from "@client/components/Icon";
import {TouchHandler} from "@client/components/TouchHandler";
import * as constants from "@client/constants";
import {resolveEntry} from "@client/features/camera";
import * as dialogs from "@client/features/dialogs";
import type {ActivityRow} from "@client/features/journal";
import {LATE_MEAL_HOUR, hourToName} from "@client/features/journal";
import * as forms from "@client/forms";
import {Locator} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {useAppDispatch, useAppSelector} from "@client/store";
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableProvidedDragHandleProps,
    Droppable,
} from "@hello-pangea/dnd";
import {PhotoIcon} from "@heroicons/react/24/outline";
import {classNames, server} from "@reactivated";
import {Decimal} from "decimal.js";
import {AnimatePresence, motion} from "motion/react";
import * as React from "react";

interface Props {
    meal: server.journal.nutrition.Meal.output;

    // Make this empty for public meals, etc.
    links?: {
        mealLink: Locator;
        getEntryLink: {
            hack(entry: server.journal.nutrition.Entry.output): string | Locator;
        }["hack"];
    };
    sorting?: {
        isDragging: boolean;
        handle: DraggableProvidedDragHandleProps | null;
    };
    hiddenMacros: boolean;
    defaultExpanded?: boolean;
}

interface EntryCardProps {
    entry: server.journal.nutrition.ExpandedEntry.output;
    index: number;
    provided: DraggableProvided;
    entryLink?: string | Locator;
}

const level2Class = "text-xs font-normal text-gray-500";

function EntryMetrics(props: {
    calculations: ReturnType<typeof getRoundedCalculations>;
}) {
    const {calculations} = props;

    return (
        <div
            className={classNames(
                "flex gap-1 tabular-nums tracking-tight *:truncate",
                level2Class,
            )}
        >
            <span className="truncate">
                P {calculations.protein}g<span className="px-1.5 text-gray-300">•</span>
                F {calculations.fat}g<span className="px-1.5 text-gray-300">•</span>C{" "}
                {calculations.carbs}g
            </span>
        </div>
    );
}

const MealEntryCard = (props: EntryCardProps) => {
    const {entry, provided, entryLink} = props;
    const useKilojoules = forms.useUseKilojoules();

    const resolved = resolveEntry(entry);

    const isLoading = resolved.kind !== "complete";
    const blockColor = resolved.kind === "error" ? "bg-red-100" : "bg-gray-200";
    const textColor = resolved.kind === "error" ? "text-red-600" : "text-gray-600";

    const calculations = getRoundedCalculations(
        calculateEntryMetrics({
            entry,
            quantity: entry.quantity,
            unit: entry.unit,
        }),
        useKilojoules,
    );

    const photoDialog = dialogs.useDialog<unknown, {image_uid: UUID}>({
        Content: (props) => {
            return (
                <img
                    alt={entry.name}
                    src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.image_uid}/public`}
                />
            );
        },
    });

    const quantityAndUnitDisplay = {
        quantity: getRoundedQuantity(entry.quantity),
        unit: entry.unit,
    };

    const photo = entry.nutritional_vision_prompt?.image?.image_uid;

    return (
        <TouchHandler
            className="relative flex flex-col gap-0 px-3 py-3.5 pl-8"
            haptic
            link={entryLink}
            role="link"
            ariaLabel={`Update ${entry.name}`}
        >
            {photoDialog.dialog}
            <div
                id={entry.uuid}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                className="flex items-center"
            >
                {entry.icon != null && (
                    <div className="-ml-6 pr-4">
                        <EntryIcon icon={entry.icon} size="journal" />
                    </div>
                )}

                {photo != null && (
                    <div className="mr-4 -ml-4 flex justify-center">
                        <button
                            type="button"
                            className="size-5"
                            onClick={(event) => {
                                console.log("clicked");
                                event.preventDefault();
                                event.stopPropagation();
                                void photoDialog({image_uid: photo});
                            }}
                        >
                            <PhotoIcon className="size-5" />
                        </button>
                    </div>
                )}
                <motion.div
                    className="flex h-10 min-w-0 flex-1 flex-col gap-1 py-0"
                    animate={isLoading ? {scale: [1, 1.02, 1]} : {scale: 1}}
                    transition={{duration: 0.3, ease: "easeInOut"}}
                >
                    <div className="-ml-1 flex items-center gap-4">
                        <AnimatePresence mode="wait" initial={false}>
                            {resolved.kind === "error" ? (
                                <motion.div
                                    key="error"
                                    initial={{opacity: 0, x: -10}}
                                    animate={{opacity: 1, x: 0}}
                                    exit={{opacity: 0, x: 10}}
                                    transition={{duration: 0.2}}
                                    className={`flex-1 text-sm font-medium ${textColor} `}
                                >
                                    Error processing
                                </motion.div>
                            ) : resolved.kind === "processing" ? (
                                <motion.div
                                    key="processing"
                                    initial={{opacity: 0, x: -10}}
                                    animate={{opacity: 1, x: 0}}
                                    exit={{opacity: 0, x: 10}}
                                    transition={{duration: 0.2}}
                                    className={`flex-1 truncate text-sm font-medium ${textColor} `}
                                >
                                    {resolved.type === "AUDIO"
                                        ? "Analyzing audio..."
                                        : "Analyzing image..."}
                                </motion.div>
                            ) : resolved.kind === "identified" ? (
                                <motion.div
                                    key="identified"
                                    initial={{opacity: 0, x: -10}}
                                    animate={{opacity: 1, x: 0}}
                                    exit={{opacity: 0, x: 10}}
                                    transition={{duration: 0.2}}
                                    className={`flex-1 truncate text-sm font-medium ${textColor} `}
                                >
                                    {resolved.name}
                                </motion.div>
                            ) : (
                                <motion.h4
                                    key="title"
                                    initial={{opacity: 0, x: -10}}
                                    animate={{opacity: 1, x: 0}}
                                    exit={{opacity: 0, x: 10}}
                                    transition={{duration: 0.2}}
                                    className="flex-1 truncate text-sm font-medium"
                                >
                                    {entry.name}
                                </motion.h4>
                            )}
                        </AnimatePresence>

                        <AnimatePresence mode="wait" initial={false}>
                            {isLoading ? (
                                <motion.div
                                    key="skeleton-cal"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className={`h-4 w-12 rounded-sm ${blockColor} animate-pulse`}
                                />
                            ) : (
                                <motion.div
                                    key="calories"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="text-xs text-gray-700"
                                >
                                    <span className="font-medium">
                                        {calculations.calories}
                                    </span>{" "}
                                    <span className="font-normal text-gray-500">
                                        {useKilojoules ? "kj" : "cal"}
                                    </span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div className="-ml-1 flex items-center gap-4">
                        <AnimatePresence mode="wait" initial={false}>
                            {isLoading ? (
                                <motion.div
                                    key="skeleton-macros"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="mt-0.5 flex w-42 gap-1.5"
                                >
                                    {[0, 1, 2].map((i) => (
                                        <motion.div
                                            key={i}
                                            initial={{opacity: 0, scale: 0.8}}
                                            animate={{opacity: 1, scale: 1}}
                                            transition={{
                                                duration: 0.2,
                                                delay: i * 0.05,
                                            }}
                                            className={`h-3 w-10 rounded-sm ${blockColor} animate-pulse`}
                                        />
                                    ))}
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="macros"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="w-42"
                                >
                                    <EntryMetrics calculations={calculations} />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <AnimatePresence mode="wait" initial={false}>
                            {isLoading ? (
                                <motion.div
                                    key="skeleton-quantity"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="flex-1 text-right"
                                >
                                    <div
                                        className={`h-3 w-16 rounded-sm ${blockColor} ml-auto animate-pulse`}
                                    />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="quantity"
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    exit={{opacity: 0, scale: 0.8}}
                                    transition={{duration: 0.2}}
                                    className="flex-1 truncate text-right text-xs font-medium text-gray-700"
                                >
                                    <span className={classNames(level2Class)}>
                                        {quantityAndUnitDisplay.quantity}{" "}
                                        {quantityAndUnitDisplay.unit}
                                    </span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </TouchHandler>
    );
};

interface CardProps {
    isExpanded: boolean;
    children: React.ReactNode;
    expandedContent?: React.ReactNode;
    rightAction?: React.ReactNode;
    caloriesDisplay?: {
        value: string;
        className: string;
    };
    dragHandle?: DraggableProvidedDragHandleProps | null;
}

const Card = (props: CardProps) => {
    const {
        isExpanded,
        children,
        expandedContent,
        rightAction,
        caloriesDisplay,
        dragHandle,
    } = props;

    return (
        <>
            <div
                {...dragHandle}
                className={classNames(
                    "flex items-center gap-2 pt-3 pb-2 px-2 mt-2.5 bg-white rounded-t-lg border-b",
                    isExpanded
                        ? "border-b border-gray-200"
                        : "rounded-b-lg border-white",
                )}
            >
                <div className="flex min-w-0 flex-1 items-center gap-4">{children}</div>
                {caloriesDisplay && (
                    <div
                        className={classNames(
                            "text-sm truncate",
                            caloriesDisplay.className,
                        )}
                    >
                        <span className="font-medium">{caloriesDisplay.value}</span>{" "}
                        <span className="font-light">cal</span>
                    </div>
                )}
                {rightAction}
            </div>
            {expandedContent !== undefined && (
                <motion.div
                    initial={isExpanded ? false : {height: 0, opacity: 0}}
                    animate={{
                        height: isExpanded ? "auto" : 0,
                        opacity: isExpanded ? 1 : 0,
                    }}
                    transition={{duration: 0.2, ease: "easeInOut"}}
                    style={{overflow: "hidden"}}
                >
                    {expandedContent}
                </motion.div>
            )}
        </>
    );
};

export const MealCard = (props: Props) => {
    const {links, meal, sorting, defaultExpanded} = props;
    const dispatch = useAppDispatch();
    const {manualMealExpansions, lastUpdatedMealUuid} = useAppSelector(
        (state) => state.search,
    );

    // Check if user has manually set expansion state for this meal
    const hasManualState = meal.uuid in manualMealExpansions;
    const isRecentlyUpdated = meal.uuid === lastUpdatedMealUuid;

    const isExpanded = hasManualState
        ? manualMealExpansions[meal.uuid]
        : isRecentlyUpdated
          ? true
          : (defaultExpanded ?? false); //  && sorting?.isDragging != true;
    const useKilojoules = forms.useUseKilojoules();

    const calculations = getRoundedCalculations(
        calculateMealMetrics(meal),
        useKilojoules,
    );

    const rendered = (
        <div {...props.sorting?.handle} className="rounded-b-xl bg-white">
            <Droppable
                droppableId={meal.uuid.toString()}
                type={constants.DROPPABLE_TYPES.ENTRY}
            >
                {(provided) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="divide-y divide-gray-200"
                    >
                        {meal.entries.map((entry, index) => (
                            <Draggable
                                key={entry.uuid}
                                draggableId={entry.uuid}
                                index={index}
                            >
                                {(provided) => (
                                    <MealEntryCard
                                        entry={entry}
                                        index={index}
                                        provided={provided}
                                        entryLink={links?.getEntryLink(entry)}
                                    />
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </div>
    );

    const {SortContext, SortProps} =
        sorting == null
            ? {SortContext: DragDropContext, SortProps: {onDragEnd: () => {}}}
            : {SortContext: React.Fragment, SortProps: {} as {onDragEnd: () => void}};

    return (
        <SortContext {...SortProps}>
            <Card
                isExpanded={isExpanded}
                dragHandle={sorting?.handle}
                caloriesDisplay={
                    calculations.calories != null
                        ? {
                              value: calculations.calories,
                              className: "text-gray-700",
                          }
                        : undefined
                }
                rightAction={
                    <button
                        className="flex size-7 items-center justify-center rounded-full bg-stone-200/40 p-1.5"
                        aria-label={isExpanded ? "Collapse meal" : "Expand meal"}
                        type="button"
                        onClick={() => {
                            dispatch({
                                type: isExpanded ? "COLLAPSE_MEAL" : "EXPAND_MEAL",
                                mealUuid: meal.uuid,
                            });
                            hardware.haptic();
                        }}
                    >
                        <motion.svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-4"
                            initial={{rotate: 180}}
                            animate={{rotate: isExpanded ? 0 : 180}}
                            transition={{duration: 0.2, ease: "easeInOut"}}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m4.5 15.75 7.5-7.5 7.5 7.5"
                            />
                        </motion.svg>
                    </button>
                }
                expandedContent={rendered}
            >
                <TouchHandler
                    className={classNames(
                        "text-center font-normal bg-stone-200/40 px-3 h-9 rounded-full flex flex-col items-center justify-center min-w-[4.2rem]",
                        meal.type == "AUTO_CREATED" ? "text-sm" : "text-xs",
                    )}
                    link={links?.mealLink}
                    haptic
                    role="button"
                >
                    {meal.type == "AUTO_CREATED" && meal.hour != null ? (
                        <span className="whitespace-nowrap">
                            {hourToName(meal.hour)}
                            {meal.hour == LATE_MEAL_HOUR && (
                                <sup
                                    aria-label="next day"
                                    className="ml-px text-[10px] font-semibold text-stone-500"
                                >
                                    +1
                                </sup>
                            )}
                        </span>
                    ) : (
                        meal.name
                    )}
                </TouchHandler>
                {(meal.type == "AUTO_CREATED" || meal.name.length < 12) && (
                    <div className="w-40">
                        <EntryMetrics calculations={calculations} />
                    </div>
                )}
            </Card>
        </SortContext>
    );
};

export function ActivityCard({activity}: {activity: ActivityRow}) {
    const isExpanded = false as boolean;
    const rendered = null;
    const durationMinutes = Math.round(activity.activity.duration_seconds / 60);

    return (
        <Card
            isExpanded={isExpanded}
            caloriesDisplay={
                activity.activity.calories != null
                    ? {
                          value: new Decimal(activity.activity.calories)
                              .toDecimalPlaces(0)
                              .toString(),
                          className: "text-red-900",
                      }
                    : undefined
            }
            rightAction={
                <span className="flex size-7 items-center justify-center rounded-full bg-red-200/40 p-1.5 text-red-800">
                    <Icon icon="bolt" />
                </span>
            }
            expandedContent={rendered}
        >
            <TouchHandler
                className={classNames(
                    "text-center font-normal bg-red-200/40 px-3 h-9 rounded-full flex flex-col items-center justify-center min-w-[4.2rem] text-red-900",
                    "text-sm",
                )}
                haptic
                role="button"
            >
                {activity.hour}
            </TouchHandler>
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-base/5">{activity.name}</span>
                <span className="text-xs font-normal text-gray-500">
                    {durationMinutes} min
                </span>
            </div>
        </Card>
    );
}
